import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import { IconClose1 } from "@velaro/velaro-component-lib";
import SkillControlsPanel from "../SkillControlsPanel";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import {
  SectionCard,
  Divider,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
} from "@/components/ds";
import {
  Lightbulb,
  ChevronDown,
  CheckCircle,
  FileText,
} from "@/components/ds/icons";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

// ServiceNow brand color
const SN_COLOR = "#81B5A1";

export interface ServiceNowConfig {
  id: number;
  siteId: number;
  displayName: string;
  instanceUrl: string;
  authMethod: string;
  clientIdMasked: string;
  usernameMasked: string;
  connectedAs: string;
  pushTranscripts: boolean;
  enabled: boolean;
  createdAt: string;
}

const CAPABILITIES = [
  {
    title: "Incident & request management",
    icon: "Tag",
    items: [
      "Create new incidents from chat data — short description, description, priority, and caller automatically populated",
      "Search and retrieve existing incidents by number, caller, or description — status, priority, and assignment group at a glance",
      "Update incident state, priority, assignment group, and work notes from chat without requiring an agent to log into ServiceNow",
      "Add work notes and close notes to incidents — keeps the audit trail complete with chat context",
      "Create and manage service requests and request items (RITMs) for catalog-based fulfillment"
    ]
  },
  {
    title: "User & CMDB lookup",
    icon: "User",
    items: [
      "Search ServiceNow users by name or email — returns department, manager, location, and contact info",
      "Get full user profile including all sys_user fields, groups, and roles during live chat",
      "Look up configuration items (CIs) in the CMDB — for IT support bots that need asset context",
      "Search asset records by serial number, name, or assigned user"
    ]
  },
  {
    title: "Knowledge base search",
    icon: "BookOpen",
    items: [
      "Search the ServiceNow Knowledge Base from inside a workflow — surface KB articles matching the visitor's issue",
      "Return article text so the AI can summarize and answer questions directly from your approved documentation",
      "Filter by knowledge base category, language, or workflow stage"
    ]
  },
  {
    title: "Change & problem management",
    icon: "RefreshCw",
    items: [
      "Create change requests from conversation data — type, risk, category, and assignment group",
      "Look up existing change requests and their approval status",
      "Create and link problem records when recurring incidents are detected during chat",
      "Get all open problems associated with a configuration item or service"
    ]
  },
  {
    title: "Catalog & task management",
    icon: "FileText",
    items: [
      "Search the service catalog and return available offerings matching the visitor's request",
      "Create catalog requests programmatically from workflow variables",
      "Create, update, and complete tasks associated with any ServiceNow record",
      "Get open tasks assigned to a user or group — useful for agent-facing IVR bots"
    ]
  }
];

const AI_TOOLS = [
  // Incidents (8)
  { name: "servicenow_create_incident", label: "Create incident", group: "Incidents", description: "Open a new incident with caller, description, priority, and category" },
  { name: "servicenow_get_incident", label: "Get incident", group: "Incidents", description: "Retrieve incident details by sys_id or number" },
  { name: "servicenow_search_incidents", label: "Search incidents", group: "Incidents", description: "Search incidents by caller, description, or state" },
  { name: "servicenow_update_incident", label: "Update incident", group: "Incidents", description: "Update state, priority, assignment group, or work notes" },
  { name: "servicenow_add_work_note", label: "Add work note", group: "Incidents", description: "Append a work note to any incident record" },
  { name: "servicenow_close_incident", label: "Close incident", group: "Incidents", description: "Close an incident with resolution code and close notes" },
  { name: "servicenow_get_user_incidents", label: "Get user incidents", group: "Incidents", description: "All incidents for a specific caller by email or sys_id" },
  { name: "servicenow_resolve_incident", label: "Resolve incident", group: "Incidents", description: "Mark incident as resolved with resolution notes" },
  // Users (4)
  { name: "servicenow_search_users", label: "Search users", group: "Users", description: "Find a user by name or email in sys_user table" },
  { name: "servicenow_get_user", label: "Get user details", group: "Users", description: "Full user profile — department, manager, location, groups" },
  { name: "servicenow_get_user_groups", label: "Get user groups", group: "Users", description: "List all groups a user belongs to" },
  { name: "servicenow_get_groups", label: "Get assignment groups", group: "Users", description: "List available assignment groups for routing" },
  // Knowledge Base (3)
  { name: "servicenow_search_knowledge", label: "Search knowledge base", group: "Knowledge Base", description: "Find KB articles matching the visitor's issue" },
  { name: "servicenow_get_article", label: "Get KB article", group: "Knowledge Base", description: "Retrieve full article text by sys_id" },
  { name: "servicenow_get_kb_categories", label: "Get KB categories", group: "Knowledge Base", description: "List knowledge base categories for filtering" },
  // Service Catalog (4)
  { name: "servicenow_search_catalog", label: "Search catalog items", group: "Service Catalog", description: "Search the service catalog by name or category" },
  { name: "servicenow_get_catalog_item", label: "Get catalog item", group: "Service Catalog", description: "Full details of a catalog item including variables" },
  { name: "servicenow_create_request", label: "Create service request", group: "Service Catalog", description: "Submit a catalog request (SC_REQUEST) on behalf of a user" },
  { name: "servicenow_get_request_status", label: "Get request status", group: "Service Catalog", description: "Check RITM status and fulfillment progress" },
  // CMDB (3)
  { name: "servicenow_search_cmdb", label: "Search CMDB", group: "CMDB", description: "Find configuration items by name, serial number, or CI class" },
  { name: "servicenow_get_ci", label: "Get CI details", group: "CMDB", description: "Retrieve full CI record including relationships and owner" },
  { name: "servicenow_get_user_assets", label: "Get user assets", group: "CMDB", description: "All assets assigned to a specific user" },
  // Change & Problem (5)
  { name: "servicenow_create_change", label: "Create change request", group: "Change & Problem", description: "Open a normal, standard, or emergency change request" },
  { name: "servicenow_get_change", label: "Get change request", group: "Change & Problem", description: "Change details by number — approval status, risk, schedule" },
  { name: "servicenow_search_changes", label: "Search change requests", group: "Change & Problem", description: "Search changes by description, state, or assignee" },
  { name: "servicenow_create_problem", label: "Create problem", group: "Change & Problem", description: "Log a new problem record from recurring incident data" },
  { name: "servicenow_get_problem", label: "Get problem", group: "Change & Problem", description: "Problem record details including known error and workaround" },
  // Tasks (4)
  { name: "servicenow_create_task", label: "Create task", group: "Tasks", description: "Create a task record (sys_task) linked to any parent record" },
  { name: "servicenow_get_tasks", label: "Get tasks", group: "Tasks", description: "Open tasks assigned to a user or group" },
  { name: "servicenow_update_task", label: "Update task", group: "Tasks", description: "Update task state, assignment, or description" },
  { name: "servicenow_complete_task", label: "Complete task", group: "Tasks", description: "Mark a task as complete with close notes" },
  // General (4)
  { name: "servicenow_table_query", label: "Query any table", group: "General", description: "Execute a sysparm_query against any ServiceNow table — for custom tables" },
  { name: "servicenow_get_record", label: "Get record", group: "General", description: "Retrieve any record by table name and sys_id" },
  { name: "servicenow_create_record", label: "Create record", group: "General", description: "Insert a new record into any ServiceNow table" },
  { name: "servicenow_update_record", label: "Update record", group: "General", description: "Update any field on any record by table name and sys_id" }
];

const AI_TOOL_GROUPS = ["Incidents", "Users", "Knowledge Base", "Service Catalog", "CMDB", "Change & Problem", "Tasks", "General"];

// Capabilities panel
function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  return (
    <div className="mb-4">
      <SectionCard
        className="!p-0"
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl"
          aria-expanded={open}
        >
          <div className="flex items-center gap-3">
            <span className="font-semibold text-zinc-800">What's included</span>
            <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
              {CAPABILITIES.length} feature areas
            </span>
          </div>
          <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="px-6 pb-6">
            <Divider />
            <div className="space-y-2 mt-4">
              {CAPABILITIES.map((cap, i) => (
                <div key={i} className="rounded-lg border border-zinc-100 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleSection(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                    aria-expanded={openSections.has(i)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-600" aria-hidden="true"><IconByName name={cap.icon} className="w-4 h-4" /></span>
                      <span className="text-sm font-medium text-zinc-700">{cap.title}</span>
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`} />
                  </button>
                  {openSections.has(i) && (
                    <div className="px-4 pb-3 space-y-1.5">
                      <Divider />
                      {cap.items.map((item, j) => (
                        <div key={j} className="flex items-start gap-2 pt-1.5">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" aria-hidden="true" />
                          <span className="text-xs text-zinc-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// AI Tools tab
function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  return (
    <SectionCard
      title="AI tools"
      description="35 tools available to any AI workflow node on this site. No per-node configuration required — the AI decides when to call ServiceNow based on conversation context."
      action={hasConnection ? <LaunchWizardButton onClick={onOpenWizard} /> : undefined}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
          {hasConnection ? "Active" : "No active connection"}
        </span>
      </div>

      <Divider />
      <div className="pt-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Available AI tools</div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">35 tools</span>
        </div>

        {AI_TOOL_GROUPS.map((group) => (
          <div key={group} className="mb-4">
            <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div
                  key={tool.name}
                  className={`flex gap-3 p-3 rounded-lg border ${hasConnection ? "border-green-200 bg-green-50" : "border-zinc-200 bg-zinc-50"}`}
                >
                  <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${hasConnection ? "bg-green-500" : "bg-zinc-300"}`} aria-hidden="true">
                    {hasConnection && (
                      <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                        <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-zinc-700">{tool.label}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{tool.description}</div>
                    <code className="text-[10px] text-zinc-400 mt-1 block">{tool.name}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Divider />
        <div className="pt-8">
          <SkillControlsPanel integration="servicenow" skills={AI_TOOLS} />
        </div>
      </div>
    </SectionCard>
  );
}

// Overview tab
function OverviewTab({ hasConnection, onConnect, onOpenWizard }: {
  hasConnection: boolean;
  onConnect(): void;
  onOpenWizard(): void;
}) {
  const IVR_PROMPT = `You are an IT Help Desk assistant for [Company Name]. You help employees report issues, check incident status, find answers in the knowledge base, and create service requests.

When an employee contacts you:
1. Ask for their name and employee email to look up their account
2. Ask them to describe their issue or request
3. Search the knowledge base first — if you find a relevant article, share the solution
4. If the issue requires a ticket, create a ServiceNow incident with the details collected
5. Confirm the incident number and expected response time

You have access to the full ServiceNow ITSM platform. You can create incidents, search KB articles, check incident status, look up CI records, and create service requests.

Keep responses concise and professional. For IVR, use clear spoken language — no markdown or bullet points.`;

  return (
    <div className="space-y-4">
      {/* Hero card */}
      <SectionCard
        title="ServiceNow ITSM"
        action={
          hasConnection ? (
            <span className="text-xs bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full">Connected</span>
          ) : (
            <PrimaryButton onClick={onConnect}>Connect</PrimaryButton>
          )
        }
      >
        <div className="text-zinc-500 text-sm leading-relaxed mb-4">
          Connect ServiceNow to give your AI workflows full ITSM context: create and search incidents, look up users and KB articles, manage service requests, query the CMDB, and log work notes — all from chat or IVR in real time.
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-teal-100 text-teal-700">35 AI tools</span>
          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">8 ITSM areas</span>
          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">OAuth 2.0 + Basic Auth</span>
          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Starter wizard included</span>
        </div>
      </SectionCard>

      {/* Capability cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CAPABILITIES.slice(0, 4).map((cap, i) => (
          <SectionCard key={i}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-zinc-600" aria-hidden="true"><IconByName name={cap.icon} className="w-5 h-5" /></span>
              <div className="font-semibold text-zinc-800 text-sm">{cap.title}</div>
            </div>
            <div className="space-y-1.5">
              {cap.items.slice(0, 3).map((item, j) => (
                <div key={j} className="flex items-start gap-2">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: SN_COLOR }} aria-hidden="true" />
                  <span className="text-xs text-zinc-600">{item}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        ))}
      </div>

      {/* IVR Quick Setup */}
      <SectionCard title="IVR help desk quick setup" description="Copy this system prompt into your IVR bot to create a working IT Help Desk voice bot in minutes. The bot will handle incident creation, KB lookup, and status checks automatically.">
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 relative">
          <pre className="text-xs text-zinc-700 whitespace-pre-wrap font-mono leading-relaxed max-h-48 overflow-y-auto">{IVR_PROMPT}</pre>
        </div>
        <div className="mt-3 flex gap-3">
          <a
            href="https://help.velaro.com/kb/articles/servicenow-bot-knowledge-guide"
            className="text-xs text-teal-600 hover:text-teal-800 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Full setup guide →
          </a>
        </div>
      </SectionCard>

      {/* How it works */}
      <SectionCard title="How it works">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { step: "1", title: "Connect your instance", body: "Choose OAuth 2.0 or Basic Auth. Enter your ServiceNow instance URL and credentials. Velaro tests the connection immediately." },
            { step: "2", title: "Add an AI workflow node", body: "Create a workflow with an AI node. All 35 ServiceNow tools are automatically available — no per-node setup needed." },
            { step: "3", title: "AI handles ITSM in real time", body: "When a user asks about their incident, requests IT help, or needs to file a ticket, the AI calls ServiceNow and responds with live data." }
          ].map((s) => (
            <div key={s.step} className="flex gap-3">
              <span
                className="flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5"
                style={{ backgroundColor: SN_COLOR }}
                aria-hidden="true"
              >
                {s.step}
              </span>
              <div>
                <div className="text-sm font-semibold text-zinc-700 mb-1">{s.title}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{s.body}</div>
              </div>
            </div>
          ))}
        </div>

        {hasConnection && (
          <>
            <div className="mt-5">
              <Divider />
            </div>
            <div className="pt-4 flex gap-3">
              <PrimaryButton onClick={onOpenWizard}>Launch starter wizard</PrimaryButton>
              <span className="text-xs text-zinc-400 self-center">Create a working ServiceNow workflow in 60 seconds</span>
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}

// Field Reference
const SN_FIELD_REFERENCE = [
  {
    scope: "Incident",
    scopeKey: "incident",
    fields: [
      { field: "sys_id", label: "Record ID", notes: "ServiceNow internal GUID" },
      { field: "number", label: "Incident Number", notes: "e.g. INC0010042" },
      { field: "short_description", label: "Short Description", notes: "One-line summary" },
      { field: "description", label: "Description", notes: "Full description / work notes" },
      { field: "state", label: "State (code)", notes: "1=New 2=In Progress 3=On Hold 6=Resolved 7=Closed" },
      { field: "priority", label: "Priority (code)", notes: "1=Critical 2=High 3=Moderate 4=Low 5=Planning" },
      { field: "urgency", label: "Urgency (code)", notes: "1=High 2=Medium 3=Low" },
      { field: "impact", label: "Impact (code)", notes: "1=High 2=Medium 3=Low" },
      { field: "category", label: "Category", notes: "e.g. inquiry / hardware / software" },
      { field: "assignment_group", label: "Assignment Group (display)", notes: "" },
      { field: "assigned_to", label: "Assigned To (display)", notes: "" },
      { field: "caller_id", label: "Caller (display)", notes: "sys_user who raised the incident" },
      { field: "sys_created_on", label: "Created Date", notes: "ISO datetime" },
      { field: "resolved_at", label: "Resolved At", notes: "ISO datetime" }
    ]
  },
  {
    scope: "Request / RITM",
    scopeKey: "request",
    fields: [
      { field: "sys_id", label: "Record ID", notes: "" },
      { field: "number", label: "Request Number", notes: "e.g. REQ0010042 or RITM0010042" },
      { field: "short_description", label: "Short Description", notes: "" },
      { field: "state", label: "State (code)", notes: "1=Pending Approval 2=In Process 3=Closed Complete 4=Closed Incomplete" },
      { field: "requested_for", label: "Requested For (display)", notes: "" },
      { field: "request_item", label: "Catalog Item", notes: "For RITMs — the catalog item name" },
      { field: "sys_created_on", label: "Created Date", notes: "ISO datetime" }
    ]
  },
  {
    scope: "User",
    scopeKey: "user",
    fields: [
      { field: "sys_id", label: "User ID", notes: "ServiceNow internal GUID" },
      { field: "user_name", label: "Username", notes: "" },
      { field: "first_name", label: "First Name", notes: "" },
      { field: "last_name", label: "Last Name", notes: "" },
      { field: "email", label: "Email", notes: "" },
      { field: "title", label: "Job Title", notes: "" },
      { field: "department", label: "Department (display)", notes: "" },
      { field: "manager", label: "Manager (display)", notes: "" },
      { field: "mobile_phone", label: "Mobile Phone", notes: "" },
      { field: "building", label: "Building", notes: "" },
      { field: "active", label: "Active", notes: "true / false" }
    ]
  },
  {
    scope: "CMDB CI",
    scopeKey: "ci",
    fields: [
      { field: "sys_id", label: "CI Record ID", notes: "" },
      { field: "name", label: "CI Name", notes: "" },
      { field: "asset_tag", label: "Asset Tag", notes: "" },
      { field: "serial_number", label: "Serial Number", notes: "" },
      { field: "model_id", label: "Model (display)", notes: "" },
      { field: "assigned_to", label: "Assigned To (display)", notes: "" },
      { field: "department", label: "Department (display)", notes: "" },
      { field: "sys_class_name", label: "Class", notes: "cmdb_ci_computer / cmdb_ci_server / etc." },
      { field: "install_status", label: "Install Status", notes: "1=Installed 2=On Order 3=In Maintenance 6=Absent 7=Retired" }
    ]
  }
];

function SnCopyVariable({ scopeKey, field }: { scopeKey: string; field: string }) {
  const [copied, setCopied] = React.useState(false);
  const variable = `{servicenow.${scopeKey}.${field}}`;
  async function copy() {
    try { await navigator.clipboard.writeText(variable); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { void e; }
  }
  return (
    <button type="button" onClick={copy} title={`Copy: ${variable}`} aria-label={`Copy variable ${variable}`}
      className={`text-[9px] px-1.5 py-0.5 rounded font-mono border transition-colors ${
        copied ? "border-green-300 bg-green-50 text-green-700" : "border-zinc-200 bg-zinc-50 text-zinc-400 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50"
      }`}
    >
      {copied ? "✓ copied" : variable}
    </button>
  );
}

function ServiceNowFieldReference() {
  const [activeScope, setActiveScope] = React.useState("Incident");
  const scopeData = SN_FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? SN_FIELD_REFERENCE[0];
  return (
    <SectionCard title="Field reference" description="Standard ServiceNow table field names and the variable syntax to use in workflow HTTP POST nodes. The agent CRM panel shows incident and user data automatically when a connection is active.">
      <div className="rounded-lg p-4 mb-5 text-xs" style={{ backgroundColor: "#edf7f4", border: `1px solid ${SN_COLOR}30` }}>
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-start gap-2" style={{ color: "#2d6a5a" }}>
            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span><strong>Agent sidebar</strong> — automatically shows the caller's open incidents and CI assignments during live chat when their email matches a ServiceNow user.</span>
          </div>
          <div className="flex items-start gap-2" style={{ color: "#2d6a5a" }}>
            <FileText className="w-4 h-4 mt-0.5 shrink-0" />
            <span><strong>HTTP POST variables</strong> — copy any variable below and paste into a workflow HTTP POST node to push incident data to other systems (Slack, email, webhooks).</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {SN_FIELD_REFERENCE.map((s) => (
          <button key={s.scope} type="button" onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeScope === s.scope ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
            }`}
            style={activeScope === s.scope ? { backgroundColor: SN_COLOR } : {}}
          >{s.scope}</button>
        ))}
      </div>
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 mb-4 text-xs">
        <div className="flex items-center justify-between mb-1.5">
          <strong className="text-zinc-700">Using these in a workflow HTTP POST node</strong>
          <span className="text-zinc-400">Click any variable to copy</span>
        </div>
        <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">{`{
  "incident_number": "{servicenow.incident.number}",
  "short_desc":      "{servicenow.incident.short_description}",
  "priority":        "{servicenow.incident.priority}",
  "caller_name":     "{servicenow.user.first_name} {servicenow.user.last_name}",
  "caller_email":    "{servicenow.user.email}",
  "department":      "{servicenow.user.department}"
}`}</pre>
      </div>
      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div className="px-4 py-2.5 text-white text-xs font-semibold" style={{ backgroundColor: "#1a1a2e" }}>
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">ServiceNow field name</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Friendly name</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Variable to copy</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">{f.field}</td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2"><SnCopyVariable scopeKey={scopeData.scopeKey} field={f.field} /></td>
                  <td className="px-3 py-2 text-zinc-400 italic text-[10px]">{f.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-zinc-400 mt-3">
        ServiceNow field names are snake_case. Reference fields (lookups) return the display value by default. To get the sys_id of a reference field, append <code className="bg-zinc-100 px-1 rounded">_link</code> to the field name in your query.
      </p>
    </SectionCard>
  );
}

// Main component
type TabId = "overview" | "config" | "skills" | "mapping" | "log";

export default function ServiceNow() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<ServiceNowConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("ServiceNow/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (e) {
      void e;
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  async function togglePushTranscripts(config: ServiceNowConfig) {
    try {
      const resp = await api.admin.put(`ServiceNow/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts,
      });
      if (!resp.ok) throw resp;
      const updated = await resp.json();
      setConfigs((prev) => prev.map((c) => (c.id === config.id ? { ...c, pushTranscripts: updated.pushTranscripts } : c)));
    } catch {
      // ignore
    }
  }

  const tabsJSX = (
    <>
      <IntegrationTab active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={activeTab === "config"} onClick={() => setActiveTab("config")}>
        Configuration
        {configs.length > 0 && (
          <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>
        )}
      </IntegrationTab>
      <IntegrationTab active={activeTab === "skills"} onClick={() => setActiveTab("skills")}>AI Skills</IntegrationTab>
      <IntegrationTab active={activeTab === "mapping"} onClick={() => setActiveTab("mapping")}>Field Reference</IntegrationTab>
      <IntegrationTab active={activeTab === "log"} onClick={() => setActiveTab("log")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="ServiceNow" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="servicenow"
          onClose={() => setShowWizard(false)}
        />
      )}

      {hasActiveConfig && (
        <SectionCard className="!bg-blue-50 !border-blue-200 mb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your ServiceNow integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/chat_window">
                <SecondaryButton>Chat window</SecondaryButton>
              </Link>
              <Link to="/deployment">
                <PrimaryButton>Get embed code</PrimaryButton>
              </Link>
            </div>
          </div>
          <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100 mt-3" style={{ backgroundColor: "rgba(255,255,255,0.6)" }}>
            <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge base Q&amp;A</strong> page widget
              from <Link to="/deployment" className="underline hover:text-blue-900">Deployments → Page widgets</Link> for
              a lightweight, AI-only Q&amp;A experience — no live agent needed.
            </span>
          </div>
        </SectionCard>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <>
              <CapabilitiesPanel />
              <OverviewTab
                hasConnection={hasActiveConfig}
                onConnect={() => setActiveTab("config")}
                onOpenWizard={() => setShowWizard(true)}
              />
            </>
          )}

          {activeTab === "config" && (
            <>
              <ServiceNowAccountsTab
                configs={configs}
                onRefresh={fetchConfigs}
                onAdd={() => navigate("/integrations/servicenow/create")}
              />

              {/* Transcript auto-push */}
              {configs.length > 0 && (
                <SectionCard title="Transcript Auto-Push" className="mb-4">
                  <div className="text-sm text-zinc-500 mb-4">
                    When enabled, the full chat transcript is automatically attached to the matched ServiceNow incident as a work note after every conversation ends.
                  </div>
                  <div className="space-y-3">
                    {configs.map((config) => (
                      <div key={config.id} className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0">
                        <div>
                          <div className="text-sm font-medium text-zinc-700">{config.displayName || config.instanceUrl}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {config.pushTranscripts
                              ? "Transcript attached to incident after each conversation"
                              : "Transcript push is off for this connection"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePushTranscripts(config)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                          aria-label={config.pushTranscripts ? "Disable transcript push" : "Enable transcript push"}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {configs.some((c) => c.pushTranscripts && c.enabled) && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                      Auto-push active — transcripts will appear as work notes on the matched ServiceNow incident after each conversation.
                    </div>
                  )}
                </SectionCard>
              )}
            </>
          )}

          {activeTab === "skills" && (
            <AiToolsTab
              hasConnection={hasActiveConfig}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}

          {activeTab === "mapping" && (
            <ServiceNowFieldReference />
          )}

          {activeTab === "log" && (
            <SectionCard title="Activity log">
              <IntegrationActivityLog integrationSlug="ServiceNow" />
            </SectionCard>
          )}
        </>
      )}
    </StickyRouteHeader>
  );
}

// Accounts tab (inline)
export function ServiceNowAccountsTab({ configs, onRefresh, onAdd }: {
  configs: ServiceNowConfig[];
  onRefresh(): void;
  onAdd(): void;
}) {
  const api = useApi();
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, { valid: boolean; message: string }>>({});
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [savingPushId, setSavingPushId] = useState<number | null>(null);
  const [pushErrors, setPushErrors] = useState<Record<number, string>>({});

  async function handleTest(id: number) {
    setTestingId(id);
    try {
      const resp = await api.admin.post(`ServiceNow/Configs/${id}/Test`, {});
      const data = await resp.json();
      setTestResults((prev) => ({
        ...prev,
        [id]: { valid: !!data?.valid, message: data?.valid ? `Connected as ${data.connectedAs || "service account"}` : (data?.error ?? "Test failed") }
      }));
    } catch (e) {
      void e;
      setTestResults((prev) => ({ ...prev, [id]: { valid: false, message: "Test failed" } }));
    } finally {
      setTestingId(null);
    }
  }

  async function handleToggle(config: ServiceNowConfig) {
    await api.admin.post(`ServiceNow/Configs/${config.id}/Toggle`, {});
    onRefresh();
  }

  async function handleTogglePushTranscripts(config: ServiceNowConfig) {
    setSavingPushId(config.id);
    setPushErrors((prev) => ({ ...prev, [config.id]: "" }));
    try {
      const resp = await api.admin.put(`ServiceNow/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts,
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setPushErrors((prev) => ({ ...prev, [config.id]: data?.error ?? `Failed to save (HTTP ${resp.status}).` }));
        return;
      }
      onRefresh();
    } catch (err) {
      setPushErrors((prev) => ({
        ...prev,
        [config.id]: `Failed to save: ${err instanceof Response ? (err as Response).status : String(err)}`,
      }));
    } finally {
      setSavingPushId(null);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Remove this ServiceNow connection? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await api.admin.delete(`ServiceNow/Configs/${id}`);
      onRefresh();
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <SectionCard
      title="Connected instances"
      action={<PrimaryButton onClick={onAdd}>Add instance</PrimaryButton>}
    >
      {configs.length === 0 ? (
        <div className="py-10 text-center">
          <div className="text-zinc-500 text-sm">No ServiceNow instances connected yet.</div>
          <div className="text-zinc-400 text-xs mt-1">Click &quot;Add instance&quot; to connect your ServiceNow environment.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 text-xs border-b border-zinc-100">
                <th className="pb-2 font-medium pr-4">Name</th>
                <th className="pb-2 font-medium pr-4">Instance URL</th>
                <th className="pb-2 font-medium pr-4">Auth</th>
                <th className="pb-2 font-medium pr-4">Transcripts</th>
                <th className="pb-2 font-medium pr-4">Status</th>
                <th className="pb-2 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {configs.map((config) => (
                <tr key={config.id}>
                  <td className="py-3 pr-4">
                    <div className="font-medium text-zinc-800">{config.displayName || "ServiceNow"}</div>
                    {config.connectedAs && <div className="text-xs text-zinc-400">as {config.connectedAs}</div>}
                  </td>
                  <td className="py-3 pr-4">
                    <a
                      href={config.instanceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-teal-600 hover:underline truncate block max-w-xs"
                    >
                      {config.instanceUrl}
                    </a>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500 capitalize">
                      {config.authMethod === "oauth" ? "OAuth 2.0" : "Basic Auth"}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <button
                      type="button"
                      onClick={() => handleTogglePushTranscripts(config)}
                      disabled={savingPushId === config.id}
                      className={`text-xs px-2 py-0.5 rounded-full disabled:opacity-50 ${config.pushTranscripts ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                      aria-label={`${config.pushTranscripts ? "Disable" : "Enable"} transcript auto-push for ${config.displayName || "ServiceNow"}`}
                    >
                      {savingPushId === config.id ? "Saving…" : config.pushTranscripts ? "Auto-push on" : "Disabled"}
                    </button>
                    {pushErrors[config.id] && (
                      <div role="alert" className="text-xs mt-0.5 text-red-600">{pushErrors[config.id]}</div>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <button
                      type="button"
                      onClick={() => handleToggle(config)}
                      className={`text-xs px-2 py-0.5 rounded-full ${config.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                      aria-label={`Toggle ${config.displayName} ${config.enabled ? "off" : "on"}`}
                    >
                      {config.enabled ? "Active" : "Disabled"}
                    </button>
                    {testResults[config.id] && (
                      <div className={`text-[10px] mt-0.5 ${testResults[config.id].valid ? "text-green-600" : "text-red-500"}`}>
                        {testResults[config.id].message}
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <GhostButton onClick={() => handleTest(config.id)} disabled={testingId === config.id}>
                        {testingId === config.id ? "Testing…" : "Test"}
                      </GhostButton>
                      <GhostButton onClick={() => handleDelete(config.id)} disabled={deletingId === config.id}>
                        {deletingId === config.id ? "Removing…" : "Remove"}
                      </GhostButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export function BackToIntegrationLandingServiceNow() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
      role="button"
      aria-label="Back to integrations"
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
